import { env } from "@/lib/env"

// The parts of Google Identity Services' OAuth 2.0 code model that sign-in uses.
// https://developers.google.com/identity/oauth2/web/reference/js-reference
interface GoogleCodeResponse {
  code?: string
  error?: string
  error_description?: string
}

interface GoogleCodeClientError {
  type: "popup_failed_to_open" | "popup_closed" | "unknown"
}

interface GoogleCodeClient {
  requestCode: () => void
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string
            scope: string
            ux_mode: "popup"
            callback: (response: GoogleCodeResponse) => void
            error_callback: (error: GoogleCodeClientError) => void
          }) => GoogleCodeClient
        }
      }
    }
  }
}

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client"

let codeClient: GoogleCodeClient | null = null

/**
 * Loads Google Identity Services and sets up the sign-in popup. The popup
 * returns an authorization code, which the backend exchanges for the user's
 * id_token.
 */
export async function setupGoogleSignIn(
  onSignIn: (code: string) => void,
  onSignInError: (error: Error) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = GOOGLE_SCRIPT_SRC
    script.async = true
    script.onload = () => {
      const oauth2 = window.google?.accounts?.oauth2
      if (!oauth2) {
        reject(
          new Error(
            "Google Identity Services loaded, but window.google.accounts.oauth2 is not available"
          )
        )
        return
      }
      try {
        codeClient = oauth2.initCodeClient({
          client_id: env("NEXT_PUBLIC_GOOGLE_CLIENT_ID") || "",
          scope: "openid email profile",
          ux_mode: "popup",
          callback: (response) => {
            if (response.code) {
              onSignIn(response.code)
              return
            }
            onSignInError(
              new Error(
                `Log in with Google failed: ${response.error_description ?? response.error}`
              )
            )
          },
          error_callback: (error) => {
            // Closing the popup is the user cancelling, not an error.
            if (error.type === "popup_closed") return
            onSignInError(new Error(`Log in with Google failed: ${error.type}`))
          },
        })
        resolve()
      } catch (error) {
        // initCodeClient throws when client_id is missing.
        reject(
          new Error("Failed to initialize Google Sign-In", { cause: error })
        )
      }
    }
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services script"))
    script.onabort = () =>
      reject(new Error("Aborted loading Google Identity Services script"))
    document.body.appendChild(script)
  })
}

/**
 * Opens Google's sign-in popup. Call it directly from a click handler, or the
 * browser blocks the popup.
 */
export function requestGoogleSignIn() {
  codeClient?.requestCode()
}
